import { QueuedMessage, WebSocketMessage } from '../../common/types';
import logger from '@ayocore/platform/common/helpers/logger';

export class MessageQueue {
  private readonly QUEUE_KEY = 'aya_messaging_queue';
  private readonly MAX_QUEUE_SIZE = 100;
  private readonly MAX_ATTEMPTS = 3;

  /**
   * Add message to localStorage queue
   */
  enqueue(webSocketMessage: WebSocketMessage): string {
    const message: QueuedMessage = {
      id: this.generateMessageId(),
      data: webSocketMessage,
      timestamp: Date.now(),
      attempts: 0,
      createdAt: new Date().toISOString(),
    };

    const queue = this.getQueue();
    queue.push(message);

    // Keep only last MAX_QUEUE_SIZE messages
    if (queue.length > this.MAX_QUEUE_SIZE) {
      const removed = queue.shift();
      logger.warn(`📦 Queue full, removed oldest message: ${removed?.id}`);
    }

    this.saveQueue(queue);
    logger.info(`📦 Message queued: ${message.id} (${queue.length} total)`);

    return message.id;
  }

  /**
   * Generate unique message ID
   */
  private generateMessageId(): string {
    const timestamp = Date.now();
    const randomSuffix = Math.random().toString(36).substring(2, 11);
    return `msg_${timestamp}_${randomSuffix}`;
  }

  /**
   * Get all queued messages for processing
   */
  getQueuedMessages(): QueuedMessage[] {
    return this.getQueue();
  }

  /**
   * Remove message from queue after successful send
   */
  dequeue(messageId: string): boolean {
    const queue = this.getQueue();
    const index = queue.findIndex(msg => msg.id === messageId);

    if (index !== -1) {
      queue.splice(index, 1);
      this.saveQueue(queue);
      logger.info(`✅ Message dequeued: ${messageId} (${queue.length} remaining)`);
      return true;
    }

    return false;
  }

  /**
   * Increment attempt count for failed message
   */
  incrementAttempts(messageId: string): boolean {
    const queue = this.getQueue();
    const message = queue.find(msg => msg.id === messageId);

    if (message) {
      message.attempts++;

      if (message.attempts >= this.MAX_ATTEMPTS) {
        // Remove message after max attempts
        this.dequeue(messageId);
        logger.error(`❌ Message ${messageId} dropped after ${this.MAX_ATTEMPTS} attempts`);
        return false;
      } else {
        this.saveQueue(queue);
        logger.warn(`⚠️ Message ${messageId} attempt ${message.attempts}/${this.MAX_ATTEMPTS}`);
        return true;
      }
    }

    return false;
  }

  /**
   * Get queue size for status display
   */
  getQueueSize(): number {
    return this.getQueue().length;
  }

  /**
   * Clear entire queue (for debugging)
   */
  clearQueue(): void {
    localStorage.removeItem(this.QUEUE_KEY);
    logger.info('🗑️ Message queue cleared');
  }

  /**
   * Get queue statistics for debugging
   */
  getQueueStats() {
    const queue = this.getQueue();
    const now = Date.now();

    return {
      total: queue.length,
      byChannel: queue.reduce(
        (acc, msg) => {
          const channel = msg.data.channelId;
          acc[channel] = (acc[channel] ?? 0) + 1;
          return acc;
        },
        {} as Record<string, number>
      ),
      oldestMessage: queue.length > 0 ? new Date(queue[0].timestamp).toISOString() : null,
      newestMessage:
        queue.length > 0 ? new Date(queue[queue.length - 1].timestamp).toISOString() : null,
      averageAge:
        queue.length > 0
          ? (now - queue.reduce((sum, msg) => sum + msg.timestamp, 0) / queue.length) / 1000
          : 0,
    };
  }

  /**
   * Загрузить очередь из localStorage.
   * Источник данных полностью контролируется приложением (сохраняем через saveQueue),
   * поэтому парсим сразу в типизированный массив без промежуточного unknown.
   */
  private getQueue(): QueuedMessage[] {
    try {
      const stored = localStorage.getItem(this.QUEUE_KEY);
      if (!stored) return [];
      const raw = JSON.parse(stored) as QueuedMessage[];
      return Array.isArray(raw) ? raw : [];
    } catch (error: unknown) {
      logger.error('❌ Failed to load message queue from localStorage:', error);
      return [];
    }
  }

  /**
   * Save queue to localStorage
   */
  private saveQueue(queue: QueuedMessage[]): void {
    try {
      localStorage.setItem(this.QUEUE_KEY, JSON.stringify(queue));
    } catch (error) {
      logger.error('❌ Failed to save message queue to localStorage:', error);
    }
  }
}
